import { useState, useEffect } from 'react'
import type { Prompt, UpdatePromptData } from '@/types'

interface PromptEditFormProps {
  prompt?: Prompt
  onSave: (data: UpdatePromptData) => void
  onCancel: () => void
  isSaving: boolean
  isCreateMode?: boolean
}

const MODEL_OPTIONS = [
  { value: 'us.amazon.nova-premier-v1:0', label: 'Amazon Nova Premier' },
]

const USE_CASE_OPTIONS = [
  { value: 'extraction', label: 'Extraction' },
  { value: 'classification', label: 'Classification' },
  { value: 'summarization', label: 'Summarization' },
]

export function PromptEditForm({
  prompt,
  onSave,
  onCancel,
  isSaving,
  isCreateMode = false,
}: PromptEditFormProps) {
  const [name, setName] = useState(prompt?.name || '')
  const [useCase, setUseCase] = useState(prompt?.use_case || 'extraction')
  const [promptText, setPromptText] = useState(prompt?.prompt_text || '')
  const [modelId, setModelId] = useState(prompt?.model_id || MODEL_OPTIONS[0].value)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (prompt) {
      setName(prompt.name)
      setUseCase(prompt.use_case)
      setPromptText(prompt.prompt_text)
      setModelId(prompt.model_id)
    }
  }, [prompt])

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!name.trim()) {
      newErrors.name = 'Name is required'
    } else if (name.length > 100) {
      newErrors.name = 'Name must be 100 characters or less'
    }

    if (!useCase.trim()) {
      newErrors.useCase = 'Use case is required'
    } else if (useCase.length > 50) {
      newErrors.useCase = 'Use case must be 50 characters or less'
    }

    if (!promptText.trim()) {
      newErrors.promptText = 'Prompt text is required'
    }

    if (!modelId.trim()) {
      newErrors.modelId = 'Model ID is required'
    } else if (modelId.length > 100) {
      newErrors.modelId = 'Model ID must be 100 characters or less'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) return

    onSave({
      name: name.trim(),
      use_case: useCase.trim(),
      prompt_text: promptText.trim(),
      model_id: modelId.trim(),
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="prompt-edit-form">
      {/* Name field */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          Prompt Name
        </label>
        <input
          type="text"
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
            errors.name
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          maxLength={100}
          data-testid="name-input"
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600" data-testid="name-error">
            {errors.name}
          </p>
        )}
      </div>

      {/* Use Case field */}
      <div>
        <label htmlFor="use-case" className="block text-sm font-medium text-gray-700">
          Use Case
        </label>
        <select
          id="use-case"
          value={useCase}
          onChange={(e) => setUseCase(e.target.value)}
          className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
            errors.useCase
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          data-testid="use-case-input"
        >
          {USE_CASE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {errors.useCase && (
          <p className="mt-1 text-sm text-red-600" data-testid="use-case-error">
            {errors.useCase}
          </p>
        )}
      </div>

      {/* Model ID field */}
      <div>
        <label htmlFor="model-id" className="block text-sm font-medium text-gray-700">
          Model ID
        </label>
        <select
          id="model-id"
          value={modelId}
          onChange={(e) => setModelId(e.target.value)}
          className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
            errors.modelId
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          data-testid="model-id-input"
        >
          {MODEL_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {errors.modelId && (
          <p className="mt-1 text-sm text-red-600" data-testid="model-id-error">
            {errors.modelId}
          </p>
        )}
      </div>

      {/* Prompt Text field */}
      <div>
        <label htmlFor="prompt-text" className="block text-sm font-medium text-gray-700">
          Prompt Text
        </label>
        <textarea
          id="prompt-text"
          value={promptText}
          onChange={(e) => setPromptText(e.target.value)}
          rows={12}
          className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm font-mono ${
            errors.promptText
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          data-testid="prompt-text-input"
        />
        {errors.promptText && (
          <p className="mt-1 text-sm text-red-600" data-testid="prompt-text-error">
            {errors.promptText}
          </p>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          data-testid="cancel-btn"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          data-testid="save-btn"
        >
          {isSaving ? 'Saving...' : isCreateMode ? 'Create Prompt' : 'Save Changes'}
        </button>
      </div>
    </form>
  )
}
